import { apiRequest } from '../../api/axiosConfig';
import {
  ChangeDetection,
  ChangeDetectionStatistics,
  ChangeEvent,
  ChangeEventQueryParams,
  CreateCompetitorPayload,
  CreateMonitoredSitePayload,
  MonitoringCompetitor,
  MonitoringSite,
  MonitoringSnapshot,
  PaginatedResponse,
  SnapshotRunSummary,
  UpdateMonitoredSitePayload,
} from '../../../types/knowledgeMonitoring';

export interface CompetitorSuggestion {
  name: string;
  company_url?: string | null;
  description?: string | null;
  strengths?: string | null;
  weaknesses?: string | null;
}

export interface CompetitorSuggestionsResponse {
  data: CompetitorSuggestion[];
  generated_at?: string | null;
}

export interface KnowledgeMonitoringService {
  getCompetitorSuggestions(productId: number): Promise<CompetitorSuggestionsResponse>;
  researchCompetitorSuggestions(productId: number): Promise<CompetitorSuggestionsResponse>;
  getCompetitors(productId: number): Promise<MonitoringCompetitor[]>;
  getSites(competitorId: number): Promise<MonitoringSite[]>;
  createCompetitor(
    productId: number,
    payload: CreateCompetitorPayload
  ): Promise<MonitoringCompetitor>;
  updateCompetitor(
    competitorId: number,
    payload: CreateCompetitorPayload
  ): Promise<MonitoringCompetitor>;
  deleteCompetitor(competitorId: number): Promise<void>;
  createSite(
    competitorId: number,
    payload: CreateMonitoredSitePayload
  ): Promise<MonitoringSite>;
  updateSite(
    siteId: number,
    payload: UpdateMonitoredSitePayload
  ): Promise<MonitoringSite>;
  deleteSite(siteId: number): Promise<void>;
  runMonitoring(siteId: number): Promise<SnapshotRunSummary>;
  getChangeEvents(
    siteId: number,
    params?: ChangeEventQueryParams
  ): Promise<PaginatedResponse<ChangeEvent>>;
  getSnapshots(
    siteId: number,
    params?: { page?: number; per_page?: number }
  ): Promise<PaginatedResponse<MonitoringSnapshot>>;
  getChangeDetections(
    siteId: number,
    params?: {
      page?: number;
      per_page?: number;
      has_changes?: boolean;
      reviewed?: boolean;
      severity?: 'high' | 'medium' | 'low';
    }
  ): Promise<PaginatedResponse<ChangeDetection>>;
  getChangeDetectionStatistics(siteId: number): Promise<ChangeDetectionStatistics>;
  getChangeDetection(changeDetectionId: number): Promise<ChangeDetection>;
  markChangeDetectionAsReviewed(
    changeDetectionId: number,
    notes?: string
  ): Promise<ChangeDetection>;
  getChangeDetectionDiffImage(changeDetectionId: number): Promise<{ url: string; expires_at: string }>;
  getProductChangeDetections(
    productId: number,
    params?: {
      page?: number;
      per_page?: number;
      has_changes_only?: boolean;
      unreviewed_only?: boolean;
      severity?: 'high' | 'medium' | 'low';
      competitor_id?: number;
      monitored_site_id?: number;
    }
  ): Promise<PaginatedResponse<ChangeDetection>>;
}

class KnowledgeMonitoringServiceImpl implements KnowledgeMonitoringService {
  async getCompetitorSuggestions(productId: number): Promise<CompetitorSuggestionsResponse> {
    return apiRequest.get<CompetitorSuggestionsResponse>(
      `knowledge-monitoring/products/${productId}/competitor-suggestions`
    );
  }

  async researchCompetitorSuggestions(productId: number): Promise<CompetitorSuggestionsResponse> {
    return apiRequest.post<CompetitorSuggestionsResponse>(
      `knowledge-monitoring/products/${productId}/competitor-suggestions`
    );
  }

  async getCompetitors(productId: number): Promise<MonitoringCompetitor[]> {
    const response = await apiRequest.get<{ data: MonitoringCompetitor[] }>(
      `knowledge-monitoring/products/${productId}/competitors`
    );

    return response.data;
  }

  async getSites(competitorId: number): Promise<MonitoringSite[]> {
    const response = await apiRequest.get<{ data: MonitoringSite[] }>(
      `knowledge-monitoring/competitors/${competitorId}/sites`
    );

    return response.data;
  }

  async createCompetitor(
    productId: number,
    payload: CreateCompetitorPayload
  ): Promise<MonitoringCompetitor> {
    const response = await apiRequest.post<{ data: MonitoringCompetitor }>(
      `knowledge-monitoring/products/${productId}/competitors`,
      payload
    );

    return response.data;
  }

  async updateCompetitor(
    competitorId: number,
    payload: CreateCompetitorPayload
  ): Promise<MonitoringCompetitor> {
    const response = await apiRequest.put<{ data: MonitoringCompetitor }>(
      `knowledge-monitoring/competitors/${competitorId}`,
      payload
    );

    return response.data;
  }

  async deleteCompetitor(competitorId: number): Promise<void> {
    await apiRequest.delete(`knowledge-monitoring/competitors/${competitorId}`);
  }

  async createSite(
    competitorId: number,
    payload: CreateMonitoredSitePayload
  ): Promise<MonitoringSite> {
    const response = await apiRequest.post<{ data: MonitoringSite }>(
      `knowledge-monitoring/competitors/${competitorId}/sites`,
      payload
    );

    return response.data;
  }

  async updateSite(
    siteId: number,
    payload: UpdateMonitoredSitePayload
  ): Promise<MonitoringSite> {
    const response = await apiRequest.put<{ data: MonitoringSite }>(
      `knowledge-monitoring/sites/${siteId}`,
      payload
    );

    return response.data;
  }

  async deleteSite(siteId: number): Promise<void> {
    await apiRequest.delete(`knowledge-monitoring/sites/${siteId}`);
  }

  async runMonitoring(siteId: number): Promise<SnapshotRunSummary> {
    const response = await apiRequest.post<{ data: SnapshotRunSummary }>(
      `knowledge-monitoring/sites/${siteId}/run`,
      {}
    );

    return response.data;
  }

  async getChangeEvents(
    siteId: number,
    params: ChangeEventQueryParams = {}
  ): Promise<PaginatedResponse<ChangeEvent>> {
    const response = await apiRequest.get<PaginatedResponse<ChangeEvent>>(
      `knowledge-monitoring/sites/${siteId}/change-events`,
      {
        params,
      }
    );

    return response;
  }

  async getSnapshots(
    siteId: number,
    params: { page?: number; per_page?: number } = {}
  ): Promise<PaginatedResponse<MonitoringSnapshot>> {
    return apiRequest.get<PaginatedResponse<MonitoringSnapshot>>(
      `knowledge-monitoring/sites/${siteId}/snapshots`,
      { params }
    );
  }

  async getChangeDetections(
    siteId: number,
    params: {
      page?: number;
      per_page?: number;
      has_changes?: boolean;
      reviewed?: boolean;
      severity?: 'high' | 'medium' | 'low';
    } = {}
  ): Promise<PaginatedResponse<ChangeDetection>> {
    return apiRequest.get<PaginatedResponse<ChangeDetection>>(
      `knowledge-monitoring/sites/${siteId}/change-detections`,
      { params }
    );
  }

  async getChangeDetectionStatistics(siteId: number): Promise<ChangeDetectionStatistics> {
    const response = await apiRequest.get<ChangeDetectionStatistics>(
      `knowledge-monitoring/sites/${siteId}/change-detections/statistics`
    );
    return response;
  }

  async getChangeDetection(changeDetectionId: number): Promise<ChangeDetection> {
    const response = await apiRequest.get<{ data: ChangeDetection }>(
      `knowledge-monitoring/change-detections/${changeDetectionId}`
    );
    return response.data;
  }

  async markChangeDetectionAsReviewed(
    changeDetectionId: number,
    notes?: string
  ): Promise<ChangeDetection> {
    const response = await apiRequest.post<{ data: ChangeDetection }>(
      `knowledge-monitoring/change-detections/${changeDetectionId}/review`,
      { notes }
    );
    return response.data;
  }

  async getChangeDetectionDiffImage(changeDetectionId: number): Promise<{ url: string; expires_at: string }> {
    const response = await apiRequest.get<{ url: string; expires_at: string }>(
      `knowledge-monitoring/change-detections/${changeDetectionId}/diff-image`
    );
    return response;
  }

  async getProductChangeDetections(
    productId: number,
    params: {
      page?: number;
      per_page?: number;
      has_changes_only?: boolean;
      unreviewed_only?: boolean;
      severity?: 'high' | 'medium' | 'low';
      competitor_id?: number;
      monitored_site_id?: number;
    } = {}
  ): Promise<PaginatedResponse<ChangeDetection>> {
    return apiRequest.get<PaginatedResponse<ChangeDetection>>(
      `knowledge-monitoring/products/${productId}/change-detections`,
      { params }
    );
  }
}

const monitoringService: KnowledgeMonitoringService = new KnowledgeMonitoringServiceImpl();

export default monitoringService;
